import { consume, consumeCode, forgetPendingSignIn, isValidEmail, pendingSignIn, rememberPendingSignIn, requestLink, type PendingSignIn, type User } from "../lib/auth.js";
import { el } from "../lib/dom.js";
import { policyLinks } from "./policyLinks.js";
import { banner, logo, messageOf, type Screen } from "./ui.js";

/** The three screens you can be on without a session, and the one dead end. */

function screen(...children: (Node | string)[]): HTMLElement {
  return el("section", { class: "screen centered", children: [logo(), ...children] });
}

/** The code screen's words (g56). The expiry rules are the API's: a 15-minute
 *  link, a 10-minute code bound to the browser that asked. */
export const CODE_SENT_TO = "If this address can sign in, a link and a code are on their way to";
export const CODE_INSTRUCTION = "Enter the six-digit code from the email";
export const CODE_HELP =
  "The code works for 10 minutes, and only in this browser. The link works once within 15 minutes. Signing in with either uses both.";

/**
 * `backLabel` names where Back goes when it is not the saved lists: a shared list's page (g72).
 * `adding`: opened by a guest's Add to my Lists (g132), whose held add only this sign-in keeps.
 */
export function signInScreen(onSignedIn: (user: User) => void, onSavedLists?: () => void, backLabel = "Back to saved lists", adding = false): Screen {
  const root = screen();
  root.classList.add("sign-in");
  const mark = root.firstChild!;
  const announcer = el("p", { class: "visually-hidden", attrs: { role: "status" } });
  // Under everything the screen paints, on the form and on the code screen (g96).
  const policies = policyLinks();
  root.append(announcer, policies);
  let disposed = false;
  let busy = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let pending: PendingSignIn | undefined;
  let failures = 0;

  const paint = (...children: (Node | string)[]) => {
    for (const child of Array.from(root.childNodes)) if (child !== mark && child !== announcer && child !== policies) child.remove();
    for (const child of children) root.insertBefore(typeof child === "string" ? document.createTextNode(child) : child, announcer);
  };
  const setBusy = (value: boolean) => {
    busy = value;
    for (const button of root.querySelectorAll("button")) button.setAttribute("aria-disabled", String(value));
    for (const input of root.querySelectorAll("input")) input.readOnly = value;
  };
  const fail = (message: HTMLElement, text: string) => {
    message.textContent = text;
    announcer.textContent = `Attempt ${++failures}. ${text}`;
  };
  const clear = () => {
    clearTimeout(timer);
    if (pending) forgetPendingSignIn(pending);
    pending = undefined;
  };
  const leave = () => {
    if (busy || disposed) return;
    clear();
    onSavedLists?.();
  };
  const back = () => onSavedLists ? el("button", {
    text: backLabel, class: "secondary", attrs: { type: "button" }, on: { click: leave },
  }) : undefined;
  const backControls = (): HTMLElement[] => { const control = back(); return control ? [control] : []; };

  async function request(address: string, message: HTMLElement): Promise<void> {
    if (busy || disposed) return;
    setBusy(true);
    try {
      const result = await requestLink(address, adding);
      if (disposed) return;
      rememberPendingSignIn(result);
      showCode(result, true);
    } catch (error) {
      if (!disposed) fail(message, messageOf(error));
    } finally {
      if (!disposed) { setBusy(false); expire(); }
    }
  }

  function showForm(address = "", notice = ""): void {
    clearTimeout(timer);
    pending = undefined;
    const email = el("input", { attrs: {
      id: "email", type: "email", inputmode: "email", autocomplete: "email",
      placeholder: "you@example.com", enterkeyhint: "send", "aria-label": "Email address",
    } });
    email.value = address;
    const message = el("p", { class: "muted", text: notice });
    const form = el("form", {
      attrs: { novalidate: true },
      children: [email, el("button", { text: "Send email", attrs: { type: "submit" } })],
      on: { submit: event => {
        event.preventDefault();
        if (busy || disposed) return;
        const address = email.value.trim();
        if (!isValidEmail(address)) { fail(message, "That doesn't look like an email address."); return; }
        void request(address, message);
      } },
    });
    paint(el("h1", { text: "Trayflux" }),
      el("p", { class: "muted", text: "Enter your email and we'll send you a sign-in link and code." }),
      form, message, ...backControls());
  }

  function expire(): void {
    if (disposed || busy || !pending || pending.expiresAt > Date.now()) return;
    const address = pending.email;
    clear();
    const notice = "The code has expired. Request a new email, or use the link if it is still valid.";
    showForm(address, notice);
    announcer.textContent = notice;
  }

  function showCode(value: PendingSignIn, focus = false): void {
    clearTimeout(timer);
    pending = value;
    const code = el("input", { attrs: {
      id: "signin-code", type: "text", inputmode: "numeric", autocomplete: "one-time-code",
      enterkeyhint: "go", "aria-describedby": "signin-code-message",
    } });
    const slots = Array.from({ length: 6 }, () => el("span", { class: "code-slot" }));
    const entry = el("div", { class: "code-entry", children: [
      el("div", { class: "code-slots", attrs: { "aria-hidden": "true" }, children: slots }), code,
    ] });
    // Keep the browser's text, caret and selection. The boxes paint only the
    // background, and malformed text stays fully editable in the same input.
    // No maxlength: native truncation runs before whitespace normalization and
    // could discard an invalid suffix after a space-padded six-digit prefix.
    const paintCode = () => {
      entry.classList.toggle("code-entry-plain", !/^[0-9]{0,6}$/.test(code.value));
      for (const [index, slot] of slots.entries()) slot.classList.toggle("filled", index < code.value.length);
    };
    const normalizeCode = () => {
      const raw = code.value;
      const normalized = raw.replace(/\s/g, "");
      if (normalized !== raw) {
        const start = raw.slice(0, code.selectionStart ?? raw.length).replace(/\s/g, "").length;
        const end = raw.slice(0, code.selectionEnd ?? raw.length).replace(/\s/g, "").length;
        const direction = code.selectionDirection;
        code.value = normalized;
        code.setSelectionRange(start, end, direction ?? undefined);
      }
      code.removeAttribute("aria-invalid");
      paintCode();
    };
    code.addEventListener("input", event => {
      if (!(event as InputEvent).isComposing) normalizeCode();
      else paintCode();
    });
    code.addEventListener("compositionend", normalizeCode);
    const message = el("p", { class: "muted", attrs: { id: "signin-code-message" } });
    const submit = el("button", { text: "Sign in", attrs: { type: "submit" } });
    const form = el("form", { attrs: { novalidate: true }, children: [
      // The instruction is the label (g56), directly above the boxes, so the
      // field and what to do with it are found before the explanation below.
      el("label", { text: CODE_INSTRUCTION, attrs: { for: "signin-code" } }), entry, submit,
    ], on: { submit: event => {
      event.preventDefault();
      if (busy || disposed) return;
      if (!/^[0-9]{6}$/.test(code.value.replace(/\s/g, ""))) {
        fail(message, "Enter the six-digit code from your email.");
        code.setAttribute("aria-invalid", "true");
        return;
      }
      code.removeAttribute("aria-invalid");
      setBusy(true);
      submit.textContent = "Signing in…";
      void consumeCode(value.email, code.value).then(result => {
        if (disposed) return;
        if (!result) {
          fail(message, "That code didn't work. Check it or request a new email.");
          code.setAttribute("aria-invalid", "true");
          code.focus();
          return;
        }
        clear();
        if (result.next) { window.location.assign(result.next); return; }
        onSignedIn(result.user);
      }).catch((error: unknown) => {
        if (!disposed) fail(message, messageOf(error));
      }).finally(() => {
        if (!disposed) { submit.textContent = "Sign in"; setBusy(false); expire(); }
      });
    } } });
    // g56: the address on a line of its own, where a long one wraps readably
    // at 320px instead of breaking the sentence around it; the neutral clause
    // stays, so the screen says nothing about whether an account exists; and
    // the expiry rules move below the form as the secondary explanation.
    paint(el("h1", { text: "Check your email" }),
    el("p", { class: "muted", children: [
      `${CODE_SENT_TO} `, el("span", { class: "sign-in-email", text: value.email }),
    ] }),
    form, message,
    el("p", { class: "muted sign-in-help", text: CODE_HELP }),
    el("button", { text: "Send again", class: "secondary", attrs: { type: "button" }, on: {
      click: () => { void request(value.email, message); },
    } }),
    el("button", { text: "Change email", class: "secondary", attrs: { type: "button" }, on: {
      click: () => {
        if (busy || disposed) return;
        clear(); showForm(value.email); root.querySelector("input")?.focus();
      },
    } }), ...backControls());
    timer = setTimeout(expire, Math.max(0, value.expiresAt - Date.now()));
    if (focus) { code.focus(); announcer.textContent = "Check your email for a sign-in link or enter the code here."; }
  }

  const restored = pendingSignIn();
  if (restored) showCode(restored); else showForm();
  document.addEventListener("visibilitychange", expire);
  window.addEventListener("focus", expire);
  return { node: root, dispose() {
    disposed = true;
    clearTimeout(timer);
    document.removeEventListener("visibilitychange", expire);
    window.removeEventListener("focus", expire);
  } };
}

/**
 * The emailed link opens this. Redeeming it is a button press, never automatic:
 * mail scanners (Outlook Safe Links, Gmail's proxy) fetch every link before a
 * human sees it, so a link that redeemed itself on load would always be spent
 * by the time it was tapped.
 */
export function confirmScreen(token: string, onSignedIn: (user: User) => void): HTMLElement {
  const root = screen();
  const message = el("p", { class: "muted" });

  const button = el("button", {
    text: "Sign in",
    on: {
      click: () => {
        button.disabled = true;
        message.textContent = "Signing in…";

        void consume(token)
          .then((result) => {
            if (result) {
              // An app was waiting to be connected. A full navigation rather
              // than a route change: the destination is the API's own consent
              // page, not a screen this client renders.
              if (result.next) {
                window.location.assign(result.next);
                return;
              }
              onSignedIn(result.user);
              return;
            }
            root.replaceChildren(
              logo(),
              el("h1", { text: "That link didn't work" }),
              el("p", { class: "muted", text: "It may have expired or already been used." }),
              el("a", { text: "Request a new one", attrs: { href: "/", "data-link": true } }),
            );
          })
          .catch((error: unknown) => {
            button.disabled = false;
            message.textContent = messageOf(error);
          });
      },
    },
  });

  root.replaceChildren(
    logo(),
    el("h1", { text: "Sign in to Trayflux" }),
    el("p", { class: "muted", text: "Tap below to finish signing in." }),
    button,
    message,
    policyLinks(),
  );
  return root;
}

/**
 * Shown when the app cannot even find out who you are. Distinct from the
 * sign-in form on purpose: being offline is not being signed out, and offering
 * a sign-in form to someone on a train sends them round a loop that cannot end.
 */
export interface UnreachableScreen extends Screen {
  retryFailed(error: unknown): void;
}

export function unreachableScreen(error: unknown, onRetry: () => Promise<void>): UnreachableScreen {
  const message = banner(messageOf(error));
  // Bare screens live outside Chrome. This region must already be mounted
  // before a retry fails; .status would hide it while empty.
  const announcer = el("p", { class: "visually-hidden", attrs: { role: "status" } });
  let pending = false;
  let disposed = false;
  let attempts = 0;

  function retryFailed(error: unknown): void {
    if (disposed) return;
    const text = messageOf(error);
    message.textContent = text;
    // Identical errors still describe separate attempts. Changing the text
    // avoids relying on a synchronous clear/reset being announced again.
    announcer.textContent = `Retry ${attempts} failed. ${text}`;
  }

  async function retry(): Promise<void> {
    if (pending || disposed) return;
    pending = true;
    attempts++;
    // Native disabled can lose focus. Keep this button in the tab order and
    // guard activation here, including keyboard and programmatic clicks.
    button.setAttribute("aria-disabled", "true");
    try {
      await onRetry();
    } catch (error) {
      retryFailed(error);
    } finally {
      if (!disposed) {
        pending = false;
        button.removeAttribute("aria-disabled");
      }
    }
  }

  const button = el("button", { text: "Try again", on: { click: () => void retry() } });
  const node = screen(
    el("h1", { text: "Trayflux" }),
    message,
    button,
    announcer,
  );
  return {
    node,
    retryFailed,
    dispose() { disposed = true; },
  };
}
